import React, { FormEventHandler, useCallback, useEffect, useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { categories as propertyCategories } from '@luxuryescapes/lib-global';

import { AccommodationTabs } from '~/components/Accommodation/Pages/Home/AccommodationHomePage';

import { InternalService, PropertyMappingMethod } from '~/services/AccommodationService';

import AccommodationCategoriesFilterInput from './AccommodationCategoriesFilterInput';
import AccommodationChainNameFilterInput from './AccommodationChainNameFilterInput';
import AccommodationCountryFilterInput from './AccommodationCountryFilterInput';
import AccommodationIndicativePropertyStateFilterInput from './AccommodationIndicativePropertyStateFilterInput';
import AccommodationInternalServiceFilterInput from './AccommodationInternalServiceFilterInput';
import AccommodationMappingMethodFilterInput from './AccommodationMappingMethodFilterInput';
import AccommodationMappingStatusFilterInput, { MappingStatusFilter } from './AccommodationMappingStatusFilterInput';
import AccommodationV2BookableFilterInput, { AccommV2BookableFilter } from './AccommodationV2BookableFilterInput';
import {
  PROMOTIONS_FILTER_OPTIONS,
  RATES_ENABLED_FILTER_OPTIONS,
  SUPPLIER_STATE_FILTER_OPTIONS,
} from './indicativePropertyStateFilters';

export interface AccommodationPropertiesSearchInput {
  propertyName?: string;
  id?: string;
  city?: string;
  country?: string;
  rating?: string;
  chainNames?: Array<string>;
  categories?: Array<propertyCategories.CategoryName>;
  chainFilterMode?: 'include' | 'exclude';
  internalService?: InternalService | '';
  ratesEnabledFilters?: string;
  supplierStateFilters?: string;
  promotionsFilters?: string;
  mappingMethods?: Array<PropertyMappingMethod>;
  mappingStatus?: MappingStatusFilter;
  accommV2Bookable?: AccommV2BookableFilter;
}

const DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT: AccommodationPropertiesSearchInput = {
  propertyName: '',
  id: '',
  city: '',
  country: '',
  rating: '',
  chainNames: [],
  categories: [],
  chainFilterMode: 'include',
  internalService: '',
  ratesEnabledFilters: '',
  supplierStateFilters: '',
  promotionsFilters: '',
  mappingMethods: [],
  mappingStatus: '',
  accommV2Bookable: '',
};

interface Props {
  initialInput?: AccommodationPropertiesSearchInput;
  onSubmit: (formObject: Record<string, string>) => void;
  selectedTab: AccommodationTabs;
}

/**
 * Search form for accommodation properties with two mutually exclusive search modes:
 * 1. Filter properties - search by name, city, country, rating, and state
 * 2. Search by ID - direct lookup by property/supplier/offer ID
 *
 * When a user focuses on one search mode, the other mode's values are cleared
 * to prevent conflicting search parameters.
 */
export default function AccommodationPropertiesSearchForm({ initialInput, onSubmit, selectedTab }: Props) {
  const [propertyName, setPropertyName] = useState<string>(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.propertyName);
  const [id, setId] = useState<string>(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.id);
  const [city, setCity] = useState<string>(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.city);
  const [country, setCountry] = useState<string>(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.country);
  const [rating, setRating] = useState<string>(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.rating);
  const [ratesEnabledFilters, setRatesEnabledFilters] = useState<string>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.ratesEnabledFilters,
  );
  const [categories, setCategories] = useState<Array<propertyCategories.CategoryName>>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.categories ?? [],
  );
  const [chainNames, setChainNames] = useState<Array<string>>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.chainNames ?? [],
  );
  const [chainFilterMode, setChainFilterMode] = useState<'include' | 'exclude'>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.chainFilterMode ?? 'include',
  );
  const [internalService, setInternalService] = useState<InternalService | ''>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.internalService,
  );
  const [supplierStateFilters, setSupplierStateFilters] = useState<string>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.supplierStateFilters,
  );
  const [promotionsFilters, setPromotionsFilters] = useState<string>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.promotionsFilters,
  );
  const [mappingMethods, setMappingMethods] = useState<Array<PropertyMappingMethod>>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.mappingMethods ?? [],
  );
  const [mappingStatus, setMappingStatus] = useState<MappingStatusFilter>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.mappingStatus ?? '',
  );
  const [accommV2Bookable, setAccommV2Bookable] = useState<AccommV2BookableFilter>(
    DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.accommV2Bookable ?? '',
  );

  // Clear ID field when user focuses on filter fields (mutual exclusivity)
  const clearSearchId = useCallback(() => {
    setId('');
  }, []);

  // Clear all filter fields when user focuses on ID search (mutual exclusivity)
  const clearSearches = useCallback(() => {
    setPropertyName('');
    setCity('');
    setCountry('');
    setRating('');
    setChainNames([]);
    setCategories([]);
    setChainFilterMode('include');
    setInternalService('');
    setRatesEnabledFilters('');
    setSupplierStateFilters('');
    setPromotionsFilters('');
    setMappingMethods([]);
    setMappingStatus('');
    setAccommV2Bookable('');
  }, []);

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();

      const formObject: Record<string, string> = {};

      if (propertyName) {
        formObject.propertyName = propertyName;
      }
      if (id) {
        formObject.id = id;
      }
      if (city) {
        formObject.city = city;
      }
      if (country) {
        formObject.country = country;
      }
      if (rating) {
        formObject.rating = rating;
      }
      if (categories.length > 0) {
        formObject.categories = categories.join(',');
      }
      if (chainNames.length > 0) {
        formObject[chainFilterMode === 'exclude' ? 'excludedChainNames' : 'chainNames'] = chainNames.join(',');
      }
      if (internalService) {
        formObject.internalService = internalService;
      }
      if (ratesEnabledFilters) {
        formObject.ratesEnabledFilters = ratesEnabledFilters;
      }
      if (supplierStateFilters) {
        formObject.supplierStateFilters = supplierStateFilters;
      }
      if (promotionsFilters) {
        formObject.promotionsFilters = promotionsFilters;
      }
      if (mappingMethods.length > 0) {
        formObject.mappingMethods = mappingMethods.join(',');
      }
      if (mappingStatus) {
        formObject.mappingStatus = mappingStatus;
      }
      if (accommV2Bookable) {
        formObject.accommV2Bookable = accommV2Bookable;
      }
      onSubmit(formObject);
    },
    [
      onSubmit,
      propertyName,
      id,
      city,
      country,
      rating,
      categories,
      chainNames,
      chainFilterMode,
      internalService,
      ratesEnabledFilters,
      supplierStateFilters,
      promotionsFilters,
      mappingMethods,
      mappingStatus,
      accommV2Bookable,
    ],
  );

  const handleReset = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();

      setPropertyName(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.propertyName);
      setId(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.id);
      setCity(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.city);
      setCountry(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.country);
      setRating(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.rating);
      setCategories(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.categories ?? []);
      setChainNames(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.chainNames ?? []);
      setChainFilterMode(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.chainFilterMode ?? 'include');
      setInternalService(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.internalService);
      setRatesEnabledFilters(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.ratesEnabledFilters);
      setSupplierStateFilters(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.supplierStateFilters);
      setPromotionsFilters(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.promotionsFilters);
      setMappingMethods(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.mappingMethods ?? []);
      setMappingStatus(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.mappingStatus ?? '');
      setAccommV2Bookable(DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.accommV2Bookable ?? '');
      onSubmit({});
    },
    [onSubmit],
  );

  useEffect(() => {
    setPropertyName(initialInput?.propertyName ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.propertyName);
    setId(initialInput?.id ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.id);
    setCity(initialInput?.city ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.city);
    setCountry(initialInput?.country ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.country);
    setRating(initialInput?.rating ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.rating);
    setCategories(initialInput?.categories ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.categories ?? []);
    setChainNames(initialInput?.chainNames ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.chainNames ?? []);
    setChainFilterMode(
      initialInput?.chainFilterMode ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.chainFilterMode ?? 'include',
    );
    setInternalService(initialInput?.internalService ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.internalService);
    setRatesEnabledFilters(
      initialInput?.ratesEnabledFilters ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.ratesEnabledFilters,
    );
    setSupplierStateFilters(
      initialInput?.supplierStateFilters ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.supplierStateFilters,
    );
    setPromotionsFilters(
      initialInput?.promotionsFilters ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.promotionsFilters,
    );
    setMappingMethods(
      initialInput?.mappingMethods ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.mappingMethods ?? [],
    );
    setMappingStatus(initialInput?.mappingStatus ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.mappingStatus ?? '');
    setAccommV2Bookable(
      initialInput?.accommV2Bookable ?? DEFAULT_ACCOMMODATION_PROPERTIES_SEARCH_INPUT.accommV2Bookable ?? '',
    );
  }, [initialInput]);

  const getIdLabel = useCallback(() => {
    if ([AccommodationTabs.UNMAPPED_SUPPLIERS, AccommodationTabs.POTENTIAL_MAPPINGS].includes(selectedTab)) {
      return 'Supplier ID / Internal Service ID';
    }
    return 'Property ID / Supplier ID / Internal Service ID / Offer ID';
  }, [selectedTab]);

  return (
    <form onSubmit={handleSubmission} onReset={handleReset}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter properties
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <TextField
                label="Property name"
                value={propertyName}
                type="text"
                onChange={(e) => setPropertyName(e.target.value)}
                onFocus={clearSearchId}
                fullWidth
              />
            </Grid>
            {[AccommodationTabs.UNMAPPED_SUPPLIERS, AccommodationTabs.POTENTIAL_MAPPINGS].includes(selectedTab) && (
              <Grid item xs={12} sm={3}>
                <AccommodationInternalServiceFilterInput value={internalService} onChange={setInternalService} />
              </Grid>
            )}
            {selectedTab === AccommodationTabs.POTENTIAL_MAPPINGS && (
              <>
                <Grid item xs={12} sm={3}>
                  <AccommodationCountryFilterInput value={country} onChange={setCountry} onFocus={clearSearchId} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationChainNameFilterInput
                    value={chainNames}
                    mode={chainFilterMode}
                    onChange={setChainNames}
                    onModeChange={setChainFilterMode}
                    onFocus={clearSearchId}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationMappingMethodFilterInput
                    value={mappingMethods}
                    onChange={setMappingMethods}
                    onFocus={clearSearchId}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationMappingStatusFilterInput value={mappingStatus} onChange={setMappingStatus} />
                </Grid>
              </>
            )}
            {selectedTab === AccommodationTabs.PROPERTIES && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="City"
                    value={city}
                    type="text"
                    onChange={(e) => setCity(e.target.value)}
                    onFocus={clearSearchId}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Country"
                    value={country}
                    type="text"
                    onChange={(e) => setCountry(e.target.value)}
                    onFocus={clearSearchId}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="star-rating-label">Star rating</InputLabel>
                    <Select
                      labelId="star-rating-label"
                      label="Star rating"
                      value={rating}
                      onChange={(e) => setRating(e.target.value)}
                      onFocus={clearSearchId}
                    >
                      <MenuItem value={3}>3</MenuItem>
                      <MenuItem value={3.5}>3.5</MenuItem>
                      <MenuItem value={4}>4</MenuItem>
                      <MenuItem value={4.5}>4.5</MenuItem>
                      <MenuItem value={5}>5</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationIndicativePropertyStateFilterInput
                    label="Rates enabled"
                    options={RATES_ENABLED_FILTER_OPTIONS}
                    value={ratesEnabledFilters}
                    onChange={setRatesEnabledFilters}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationIndicativePropertyStateFilterInput
                    label="Supplier state"
                    options={SUPPLIER_STATE_FILTER_OPTIONS}
                    value={supplierStateFilters}
                    onChange={setSupplierStateFilters}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationIndicativePropertyStateFilterInput
                    label="Promotions"
                    options={PROMOTIONS_FILTER_OPTIONS}
                    value={promotionsFilters}
                    onChange={setPromotionsFilters}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationV2BookableFilterInput
                    value={accommV2Bookable}
                    onChange={setAccommV2Bookable}
                    onFocus={clearSearchId}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AccommodationCategoriesFilterInput
                    value={categories}
                    onChange={setCategories}
                    onFocus={clearSearchId}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="text" type="reset">
                Reset
              </Button>
              <Button variant="contained" type="submit">
                Search
              </Button>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
            Or search by ID
          </Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                label={getIdLabel()}
                value={id}
                type="text"
                size="small"
                onChange={(e) => setId(e.target.value)}
                onFocus={clearSearches}
                fullWidth
              />
            </Grid>
            <Grid item>
              <Button variant="outlined" type="submit" size="small">
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  );
}
